import "react-datepicker/dist/react-datepicker.css";

import { CheckCircleIcon, XIcon } from "@heroicons/react/solid";
import {
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	PrinterIcon,
} from "@heroicons/react/outline";
import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { endOfMonth, format, startOfMonth } from "date-fns";
import DatePicker from "react-datepicker";
import { PrintButtonIcon } from "src/pages/billet/styles";
import { Skeleton } from "../../../components/backoffices/Skeleton";
import { Transition } from "@headlessui/react";
import { api } from "../../../services/api";
import { currencyFormat } from "../../../utils/currencyFormat";
import { ExportPDF_V2_BACKOFFICE } from "../Export/pdf";
import ptBR from "date-fns/locale/pt-BR";
import { ExtractItem } from "src/utils/extractDescription";
import { getTransactionNames } from "src/components/extractPdf";
import transactionLabel from "src/utils/transactionLabel";
import { config } from "src/config";
import { useAuth } from "src/hooks/auth";
import handleExportExcelBackoffice from "src/utils/excel/backoffice";
import { separateTransactions } from "src/utils/separateTransactions";
import { getExtract } from "src/requests/export-extract";

export interface Transaction {
	id: string;
	credit: boolean;
	amount: number;
	type: string;
	typeId: string;
	description: string;
	status: string;
	balanceAfter: number;
	date: string;
}

export interface Detail {
	id: string;
	credit: boolean;
	amount: number;
	description: string;
	rateValue: number;
	consignorName: string;
	consignorDocument: string;
	consignorBankNumber: string;
	consignorBankBranch: string;
	consignorBankAccount: string;
	consignorBankAccountDigit: string;
	beneficiaryName: string;
	beneficiaryDocument: string;
	beneficiaryBankNumber: string;
	beneficiaryBankBranch: string;
	beneficiaryBankAccount: string;
	beneficiaryBankAccountDigit: string;
	status: string;
	date: string;
}

export interface MetaProps {
	current_page: number;
	total_pages: number;
	total_items: number;
	total_items_page: number;
}

export function TransactionsWallet() {
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [isExportingPDF, setIsExportingPDF] = useState(false);
	const [isExportingExcel, setIsExportingExcel] = useState(false);
	const [items, setItems] = useState<ExtractItem[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState(1);
	const navigate = useNavigate();

	const start = startOfMonth(new Date());
	const end = endOfMonth(new Date());

	const [startDate, setStartDate] = useState(start);
	const [endDate, setEndDate] = useState(end);

	const [show, setShow] = useState(false);

	const token2 = window.localStorage.getItem("@backoffice:token");
	const perPage = 10;

	const { account, user } = useAuth();

	const loadTransfers = async (page: number) => {
		setLoading(true);

		try {
			let url = `/backoffice/transactions`;
			const { data } = await api.get(url, {
				params: {
					page: currentPage,
					perPage: perPage,
					role: "WALLET",
				},
				headers: { Authorization: "Bearer " + token2 },
			});

			const transactions = data.transactions;

			const filteredTransactions = transactions.filter((transaction: any) => {
				const transactionDate = new Date(transaction.created_at);
				return transactionDate >= startDate && transactionDate <= endDate;
			});

			const { orderedTransactions } =
				separateTransactions(filteredTransactions);

			setItems(orderedTransactions);
			setTotal(filteredTransactions.length);

			setLoading(false);
		} catch (err) {
			console.log(err);

			setLoading(false);
		}
	};

	useEffect(() => {
		(async () => {
			await loadTransfers(currentPage);
		})();
	}, [currentPage, id]);

	const handleClickExportPDF = () => {
		handleExport(items, "pdf");
	};

	const handleClickExportExcel = () => {
		handleExport(items, "excel");
	};

	async function handleExport(items: ExtractItem[], type: string) {
		const startDateFormatted = startDate.toISOString().split('T')[0];  // "2023-06-01"
		const endDateFormatted = endDate.toISOString().split('T')[0];      // "2025-06-30"
		if (type === "pdf") {
			setIsExportingPDF(true);
			const transaction = await getExtract(startDateFormatted, endDateFormatted, "all", "pdf").finally(() => {
				setIsExportingPDF(false)
			})

			console.log(transaction)

			const linkSource = `data:application/pdf;base64,${transaction?.pdfBase64}`;
			const downloadLink = document.createElement("a");

			downloadLink.href = linkSource;
			downloadLink.download = `extrato.pdf`;
			downloadLink.click();
		} else if (type === "excel") {
			await getExtract(startDateFormatted, endDateFormatted, "all", "excel");
		}
	}

	function getType(type: string): string {
		if (type === "PIX") {
			return "Pix";
		}

		if (type === "TED") {
			return "Transferência";
		}

		if (type === "P2P") {
			return "Transferência";
		}

		if (type === "BILLET") {
			return "Boleto";
		}

		if (type === "PAYMENT") {
			return "Pagamento";
		}

		if (type === "INITIAL") {
			return "";
		}

		return type;
	}

	async function handlePrevious() {
		const page = currentPage - 1;
		if (page === 0) return;
		setCurrentPage(page);
	}

	const totalPages = Math.ceil(total / perPage);

	function handleNext() {
		if (currentPage < totalPages) {
			setCurrentPage(currentPage + 1);
		}
	}

	const dateFormat = (date: string | undefined): string => {
		// Formata a data para o formato desejado
		const formattedDate = format(new Date(date || ""), "dd/MM/yyyy HH:mm", {
			locale: ptBR,
		});
		return formattedDate;
	};

	const startIdx = (currentPage - 1) * perPage;
	const endIdx = startIdx + perPage;
	const paginatedItems = items.slice(startIdx, endIdx);

	console.log(paginatedItems);

	return (
		<div>
			<div className="px-4 sm:px-6 lg:px-8">
				<h1 className="text-xl font-semibold text-gray-900">Extrato: Wallet</h1>
				<section aria-labelledby="">
					<>
						<div>
							<div>
								<Skeleton isActive={loading} repeat={3} />
								{!loading && (
									<>
										<div>
											<span>Selecione um período para filtrar</span>
											<div className="flex items-center">
												<DatePicker
													className="bg-gray-300 rounded px-2 py-1 mr-2"
													selected={startDate}
													onChange={(date: Date) => setStartDate(date)}
													locale="pt"
													dateFormat="dd/MM/yyyy"
												/>
												<span className="mx-2">até</span>
												<DatePicker
													className="bg-gray-300 rounded px-2 py-1 mr-2"
													selected={endDate}
													onChange={(date: Date) => setEndDate(date)}
													locale="pt"
													dateFormat="dd/MM/yyyy"
												/>
												<button
													className="ml-2 bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
													onClick={() => loadTransfers(1)}
												>
													Filtrar
												</button>
											</div>
										</div>
										<div className="sm:flex sm:items-center">
											<div className="sm:flex-auto"></div>
											<div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
												<button
													type="button"
													onClick={handleClickExportPDF}
													className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto mr-3"
												>
													{isExportingPDF ? "Exportando..." : "Exportar PDF"}
												</button>

												<button
													type="button"
													onClick={handleClickExportExcel}
													className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
												>
													{isExportingExcel
														? "Exportando..."
														: "Exportar Excel"}
												</button>
											</div>
										</div>
										<div className="mt-8 flex flex-col">
											<div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
												<div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
													<div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
														<table className="min-w-full divide-y divide-gray-300">
															<thead className="bg-gray-50">
																<tr>
																	<th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
																		Data
																	</th>
																	<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
																		Tipo
																	</th>
																	<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
																		Transação
																	</th>
																	<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
																		Beneficiário
																	</th>
																	<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
																		Valor
																	</th>
																	<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
																		Imprimir
																	</th>
																</tr>
															</thead>
															<tbody className="bg-white">
																{!loading &&
																	paginatedItems.map((item) => {
																		const names = getTransactionNames(item);

																		let displayName


																		if (item.direction !== "in") {
																			if (item.data.beneficiaryName) {
																				displayName = item.data.beneficiaryName
																			} else {
																				displayName = item.data.transfer.destination_bank_account?.beneficiaryName
																			}
																		} else {
																			console.log(item.data)
																			if (item.data && item.data.data.payer) {

																				displayName = item.data.data.payer.name
																			}
																		}

																		if (
																			item.status == "waiting" ||
																			item.status == "PENDING"
																		)
																			return null;

																		return (
																			<tr
																				key={item.id}
																				onClick={() => {
																					// showTransaction(item as Transaction)
																				}}
																			>
																				<td className="whitespace-nowrap py-4 pl-4 text-sm">
																					{dateFormat(item.created_at)}
																				</td>
																				<td className="px-3 py-4 text-sm">
																					{item.direction === "in"
																						? "Crédito"
																						: "Débito"}
																				</td>
																				<td className="px-1 py-4 text-sm">
																					{transactionLabel(item)}
																				</td>
																				<td className="px-3 py-4 text-sm">
																					{item.direction === "in"
																						? `De ${displayName}`
																						: `Para ${displayName}`}
																				</td>
																				<td className="px-3 py-4 text-sm">
																					<span
																						style={{
																							color:
																								item.direction === "in"
																									? "var(--green)"
																									: "var(--boxText)",
																							marginLeft:
																								item.direction === "in"
																									? "8px"
																									: "0",
																						}}
																					>
																						{currencyFormat(
																							Number(item.amount),
																						)}
																					</span>
																					<span
																						style={{
																							marginLeft: "20px",
																							display:
																								item.direction === "in"
																									? "none"
																									: "inline",
																						}}
																					></span>
																				</td>
																				<td className="px-6 py-4 text-sm">
																					<PrintButtonIcon>
																						<PrinterIcon
																							onClick={() => {
																								const receiptString =
																									JSON.stringify(item);

																								localStorage.setItem(
																									"temp_receipt_to_print",
																									receiptString,
																								);

																								navigate("/PDF");
																							}}
																							color="var(--boxText)"
																							className="h-4 w-4 inline"
																						/>
																					</PrintButtonIcon>
																				</td>
																			</tr>
																		);
																	})}
															</tbody>
														</table>
														<div className="flex items-center justify-center mt-5 mb-5">
															<ChevronDoubleLeftIcon
																onClick={handlePrevious}
																className="mr-3 cursor-pointer w-5 h-5 text-gray-500"
															/>
															<span>
																Página {currentPage} de {totalPages}
															</span>
															<ChevronDoubleRightIcon
																onClick={handleNext}
																className="ml-3 cursor-pointer w-5 h-5 text-gray-500"
															/>
														</div>
													</div>
												</div>
											</div>
										</div>
									</>
								)}
							</div>
						</div>

						<div
							aria-live="assertive"
							className="z-50 fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start"
						>
							<div className="w-full flex flex-col items-center space-y-4 sm:items-end">
								{/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
								<Transition
									show={show}
									as={Fragment}
									enter="transform ease-out duration-300 transition"
									enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
									enterTo="translate-y-0 opacity-100 sm:translate-x-0"
									leave="transition ease-in duration-100"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
								>
									<div className="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
										<div className="p-4">
											<div className="flex items-start">
												<div className="flex-shrink-0">
													<CheckCircleIcon
														className="h-6 w-6 text-green-400"
														aria-hidden="true"
													/>
												</div>
												<div className="ml-3 w-0 flex-1 pt-0.5">
													<p className="text-sm font-medium text-gray-900">
														Configurações salvar
													</p>
													<p className="mt-1 text-sm text-gray-500">
														As configurações de taxas foram alteradas.
													</p>
												</div>
												<div className="ml-4 flex-shrink-0 flex">
													<button
														type="button"
														className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
														onClick={() => {
															setShow(false);
														}}
													>
														<span className="sr-only">Close</span>
														<XIcon className="h-5 w-5" aria-hidden="true" />
													</button>
												</div>
											</div>
										</div>
									</div>
								</Transition>
							</div>
						</div>
					</>
				</section>
			</div>
		</div>
	);
}
